import { jsxs, jsx } from 'react/jsx-runtime';
import { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { useHoverDirty, useEffectOnce } from 'react-use';
import './components/ButtonGroup/index.mjs';
import { Product } from './components/Product/Product.mjs';
import { groupProducts } from './components/ProductGroups/groupProducts.mjs';
import { ProductGroups } from './components/ProductGroups/ProductGroups.mjs';
import { ScrollButton } from './components/ScrollButton/ScrollButton.mjs';
import { PRODUCT_CARD_WIDTH, PRODUCT_LIST_GRID_GAP, Wrapper, Container, ProductGroupProductListContainer, ProductList as ProductList$1 } from './ProductList.styles.mjs';
import { useIsAtMaxScroll } from './useIsAtMaxScroll.mjs';
import { ButtonGroup } from './components/ButtonGroup/ButtonGroup.mjs';
"use strict";
const CHECK_FOR_SCROLL_TIMEOUT = 150;
const ProductList = ({
  products,
  selectedProducts,
  setSelectedProducts,
  hasBeenObserved,
  selectedProductsLimit,
  initialActiveGroup,
  setExternalState,
  showProductGroups,
  isOverviewPage
}) => {
  const groupedGroups = useMemo(() => groupProducts(products), [products]);
  const moreThanOneGroupExists = groupedGroups.length > 1;
  const containerRef = useRef(null);
  const productListRef = useRef(null);
  const [buttonHasBeenClicked, setButtonHasBeenClicked] = useState(false);
  const [activeGroup, setActiveGroup] = useState(initialActiveGroup);
  const [playAnimation, setPlayAnimation] = useState(false);
  const isHovering = useHoverDirty(
  // Hack: Type issue with ref on useHoverDirty side - https://github.com/streamich/react-use/issues/2379
  containerRef);
  const [isDragging, setIsDragging] = useState(false);
  const [overflowExists, setOverflowExists] = useState(true);
  const [endPosition, setEndPosition] = useState(0);
  useEffectOnce(() => {
    if (containerRef.current && productListRef.current && containerRef.current?.getBoundingClientRect().width < productListRef.current?.getBoundingClientRect().width) {
      setOverflowExists(true);
    } else {
      setOverflowExists(false);
    }
  });
  useEffect(() => {
    setActiveGroup(products[0]?.productListGroup?.id);
  }, [products]);
  useEffect(() => {
    if (!showProductGroups && hasBeenObserved) {
      setPlayAnimation(true);
    }
  }, [hasBeenObserved, setPlayAnimation, showProductGroups]);
  useEffect(() => {
    const productListInitialPosition = productListRef.current?.getBoundingClientRect().left;
    let timeoutId;
    if (hasBeenObserved || buttonHasBeenClicked) {
      timeoutId = setTimeout(() => {
        const currentPosition = productListRef.current?.getBoundingClientRect().left;
        if (productListInitialPosition === currentPosition) {
          return setPlayAnimation(true);
        }
      }, CHECK_FOR_SCROLL_TIMEOUT);
    }
    return () => {
      clearTimeout(timeoutId);
    };
  }, [hasBeenObserved, setPlayAnimation, buttonHasBeenClicked]);
  const dragStartingPosition = useRef(0);
  const scrollAmount = useRef(0);
  const dragHandler = useCallback(event => {
    scrollAmount.current = dragStartingPosition.current - event.clientX;
    containerRef.current?.scrollTo(endPosition + scrollAmount.current, 0);
  }, [endPosition]);
  const mouseUpHandler = useCallback(() => {
    setIsDragging(false);
    setEndPosition(containerRef.current?.scrollLeft ?? 0);
    document.removeEventListener("mousemove", dragHandler);
    document.removeEventListener("mouseup", mouseUpHandler);
  }, [dragHandler]);
  const mouseDownHandler = useCallback(event => {
    if (event.buttons === 1) {
      dragStartingPosition.current = event.clientX;
      setIsDragging(true);
      document.addEventListener("mousemove", dragHandler);
      document.addEventListener("mouseup", mouseUpHandler);
    }
  }, [dragHandler, mouseUpHandler]);
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let isScrolling;
    const checkIsScrolling = () => {
      window.clearTimeout(isScrolling);
      isScrolling = setTimeout(function () {
        setPlayAnimation(true);
        setEndPosition(containerRef.current?.scrollLeft);
      }, CHECK_FOR_SCROLL_TIMEOUT);
    };
    container.addEventListener("scroll", checkIsScrolling);
    return () => {
      window.removeEventListener("resize", checkIsScrolling);
    };
  }, []);
  const tabClickHandler = useCallback(({
    groupId
  }) => {
    const initialScrollPosition = containerRef.current.scrollLeft;
    const checkForScroll = () => {
      const currentScrollPosition = containerRef.current.scrollLeft;
      return currentScrollPosition === initialScrollPosition;
    };
    const element = document.getElementById(groupId);
    setPlayAnimation(false);
    setActiveGroup(groupId);
    setButtonHasBeenClicked(true);
    element?.scrollIntoView({
      behavior: "smooth",
      inline: "start",
      block: "nearest"
    });
    setTimeout(() => {
      if (checkForScroll()) {
        setPlayAnimation(true);
      }
    }, CHECK_FOR_SCROLL_TIMEOUT);
  }, [containerRef, setPlayAnimation, setActiveGroup]);
  const scrollButtonHandler = ({
    isLeftArrow
  }) => {
    if (containerRef.current) {
      const currentScrollPosition = containerRef.current?.scrollLeft;
      const scrollLength = isLeftArrow ? currentScrollPosition - PRODUCT_CARD_WIDTH - PRODUCT_LIST_GRID_GAP : currentScrollPosition + PRODUCT_CARD_WIDTH + PRODUCT_LIST_GRID_GAP;
      containerRef.current?.scrollTo(scrollLength, 0);
    }
  };
  const {
    isMaxLeft,
    isMaxRight
  } = useIsAtMaxScroll(containerRef);
  return /* @__PURE__ */jsxs(Wrapper, {
    children: [showProductGroups && moreThanOneGroupExists && /* @__PURE__ */jsx(ButtonGroup, {
      items: groupedGroups.map(group => group.productListGroup),
      activeButton: activeGroup,
      onClick: id => {
        tabClickHandler({
          groupId: id
        });
      }
    }), /* @__PURE__ */jsx(Container, {
      ref: containerRef,
      $isHovering: isHovering,
      $moreThanOneGroupExists: moreThanOneGroupExists,
      $isDragging: isDragging,
      children: /* @__PURE__ */jsxs(ProductGroupProductListContainer, {
        children: [moreThanOneGroupExists && /* @__PURE__ */jsx(ProductGroups, {
          products,
          activeGroup,
          playAnimation,
          canPlayAniimation: hasBeenObserved || buttonHasBeenClicked,
          showProductGroups
        }), /* @__PURE__ */jsxs(ProductList$1, {
          $columns: products.length,
          $isDragging: isDragging,
          $overflowExists: overflowExists,
          $moreThanOneGroupExists: moreThanOneGroupExists,
          onMouseDown: event => mouseDownHandler(event),
          onMouseUp: mouseUpHandler,
          ref: productListRef,
          children: [/* @__PURE__ */jsx(ScrollButton, {
            color: "white",
            rotationDegrees: 180,
            isVisible: !isMaxLeft,
            onClick: () => scrollButtonHandler({
              isLeftArrow: true
            })
          }), /* @__PURE__ */jsx(ScrollButton, {
            color: "white",
            rotationDegrees: 0,
            isVisible: isMaxRight,
            isRightButton: true,
            onClick: () => scrollButtonHandler({
              isLeftArrow: false
            })
          }), products.map((product, productsArrayIndex) => {
            const groupIsActive = product.productListGroup?.id === activeGroup;
            const productListGroup = products.filter(product2 => activeGroup === product2.productListGroup?.id);
            const groupIndex = productListGroup.findIndex(item => item.id === product.id);
            return /* @__PURE__ */jsx(Product, {
              onClick: () => setActiveGroup(product.productListGroup?.id),
              groupIndex,
              groupLength: productListGroup.length,
              id: product?.id,
              groupId: product.productListGroup?.id,
              image: product.image ?? {
                src: "",
                width: 0,
                height: 0
              },
              name: product?.productTitle,
              description: product.description,
              selectedProducts,
              setSelectedProducts,
              setExternalState,
              groupIsActive,
              playAnimation,
              canPlayAniimation: hasBeenObserved || buttonHasBeenClicked,
              selectedProductsLimit,
              showProductGroups,
              isStaticProduct: productsArrayIndex === 0,
              isOverviewPage
            }, product.id);
          })]
        })]
      })
    })]
  });
};
export { ProductList };
//# sourceMappingURL=ProductList.mjs.map
